import { useState, useRef, useEffect } from "react";
import ContentEditable from "react-contenteditable";
import DOMPurify from "dompurify";
import {
  LucidePaperclip,
  SmileIcon,
  FileSpreadsheet,
  FileText,
  File,
} from "lucide-react";
import { ArrowPathIcon, XCircleIcon } from "@heroicons/react/16/solid";
import { v4 as uuidv4 } from "uuid";
import { Message } from "../../types/message";
import { Attachment } from "../../types/attchment";
// @ts-ignore
const VITE_API_URL = import.meta.env.VITE_API_URL;

// Helper function to get file type
const getFileType = (file: File) => {
  const fileType = file.type.split("/")[0];
  if (fileType === "image" || fileType === "video" || fileType === "audio") {
    return fileType;
  }
  return "file";
};

const allowedTags = ["b", "i", "strong", "em", "a", "img", "video"];
const allowedAttributes = ["href", "target", "src", "alt", "controls"];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_FILES = 3;

interface InputBoxProps {
  onSend: (message: Message) => void;
  disable?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const InputBox: React.FC<InputBoxProps> = ({ onSend, disable, onChange }) => {
  const [html, setHtml] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const contentEditableRef = useRef(null);
  const fileInputRef = useRef<HTMLInputElement>(null); // Added ref for file input

  // Function to handle content change
  const handleChange = async (e: any) => {
    onChange(e);
    const cleanHtml = DOMPurify.sanitize(e.target.value, {
      ALLOWED_TAGS: allowedTags,
      ALLOWED_ATTR: allowedAttributes,
    });

    // Check for pasted images (data URLs)
    const { updatedContent, newAttachments } = await handlePastedContent(
      cleanHtml
    );

    setHtml(updatedContent);

    if (newAttachments.length > 0) {
      setAttachments((prev) => [...prev, ...newAttachments]);
    }
  };

  // Function to handle pasted content and upload images
  const handlePastedContent = async (content: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    const imgTags = doc.getElementsByTagName("img");
    const newAttachments: Attachment[] = [];

    // @ts-ignore
    for (let img of imgTags) {
      const src = img.getAttribute("src");
      if (src && src.startsWith("data:")) {
        // It's a data URL, upload the image
        try {
          const { attachment, uploadPromise } = await uploadPastedImage(src);
          // Set the image src to the uploaded URL
          img.setAttribute("src", attachment.url);
          newAttachments.push(attachment);

          // Handle upload progress (optional for pasted images)
          uploadPromise.catch((error) => {
            console.error("Error uploading image", error);
            // Remove the image from the content
            img.parentNode?.removeChild(img);
            setAttachments((prev) =>
              prev.map((att) =>
                att.uid === attachment.uid ? { ...att, status: "failed" } : att
              )
            );
          });
        } catch (error) {
          console.error("Error uploading image", error);
          // Remove the image from the content
          img.parentNode?.removeChild(img);
        }
      }
    }

    const updatedContent = doc.body.innerHTML;
    return { updatedContent, newAttachments };
  };

  // Function to upload pasted images to server
  const uploadPastedImage = async (imageData: string) => {
    // Convert base64 data URL to Blob
    const response = await fetch(imageData);
    const blob = await response.blob();

    const uid = uuidv4(); // Generate UUID v4 for the file
    const filename = `image_${uid}.png`; // Use UUID in the filename

    // Create the attachment object
    const attachment: Attachment = {
      uid: uid,
      url: "", // Will be updated after upload
      localUrl: imageData, // Use data URL for preview
      type: "image",
      filename: filename,
      progress: 0,
      status: "uploading",
      file: blob, // Store blob for retry
    };

    // Add to attachments
    setAttachments((prev) => [...prev, attachment]);

    // Start the upload
    const uploadPromise = uploadFileToServer(
      blob,
      filename,
      attachment.uid || "",
      attachment.type || ""
    );

    return { attachment, uploadPromise };
  };

  const uploadFileToServer = async (
    file: File | Blob,
    filename: string,
    attachmentId: string,
    fileType: string
  ): Promise<void> => {
    try {
      const uid = attachmentId; // Use attachment ID as UID
      const filetype = file.type || "application/octet-stream";

      const formData = new FormData();
      formData.append("name", filename);
      formData.append("file", file);

      const queryParams = new URLSearchParams({
        uid,
        filetype,
        filename: encodeURIComponent(filename),
      });

      const headers: any = {};
      console.log(headers);
      return new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.open(
          "POST",
          `${VITE_API_URL}/auth/upload-file?${queryParams.toString()}`,
          true
        );

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            setAttachments((prev) =>
              prev.map((att) =>
                att.uid === attachmentId ? { ...att, progress } : att
              )
            );
          }
        };

        // Handle successful upload
        xhr.onload = () => {
          if (xhr.status === 200) {
            try {
              const response = JSON.parse(xhr.responseText);
              const publicUrl = response.url; // Assuming the response includes the public URL in 'url' field

              setAttachments((prev) =>
                prev.map((att) =>
                  att.uid === attachmentId
                    ? {
                        ...att,
                        url: publicUrl,
                        status: "uploaded",
                        localUrl: "", // Remove local URL as we now have the public URL
                        file: undefined, // Remove file reference
                      }
                    : att
                )
              );
              resolve();
            } catch (parseError) {
              console.error("Failed to parse response:", parseError);
              setAttachments((prev) =>
                prev.map((att) =>
                  att.uid === attachmentId ? { ...att, status: "failed" } : att
                )
              );
              reject("Invalid server response");
            }
          } else {
            // Upload failed with server error
            console.error(`Upload failed with status ${xhr.status}`);
            setAttachments((prev) =>
              prev.map((att) =>
                att.uid === attachmentId ? { ...att, status: "failed" } : att
              )
            );
            reject(`Upload failed with status ${xhr.status}`);
          }
        };

        // Handle network errors
        xhr.onerror = () => {
          console.error("Network error during file upload");
          setAttachments((prev) =>
            prev.map((att) =>
              att.uid === attachmentId ? { ...att, status: "failed" } : att
            )
          );
          reject(new Error("Network error"));
        };

        // Send the request with form data
        xhr.send(formData);
      });
    } catch (error) {
      // Properly propagate the error
      throw error;
    }
  };

  // Function to handle file selection or drop
  const handleFilesAdded = async (newFiles: File[]) => {
    const validFiles = newFiles.filter((file) => file.size <= MAX_FILE_SIZE);

    if (validFiles.length !== newFiles.length) {
      alert("Some files exceed the 10MB size limit and were not added.");
    }

    if (attachments.length + validFiles.length > MAX_FILES) {
      alert(`You can only upload up to ${MAX_FILES} files.`);
      return;
    }

    validFiles.forEach((file) => {
      const uid = uuidv4(); // Generate UUID v4 for the file
      const filename = `${uid}_${file.name}`; // Use UUID in the filename
      const localUrl = URL.createObjectURL(file);

      const attachment: Attachment = {
        uid: uid,
        url: "", // Will be updated after upload
        localUrl,
        type: getFileType(file),
        filename: file.name,
        progress: 0,
        status: "uploading",
        file, // Store file for retry
      };

      // Add to attachments
      setAttachments((prev) => [...prev, attachment]);

      // Start the upload
      uploadFileToServer(file, filename, uid, attachment?.type || "");
    });
  };

  // Function to handle file input change
  const handleFileChange = (e: any) => {
    const newFiles: any = Array.from(e.target.files);
    handleFilesAdded(newFiles);

    // Reset the input value to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Function to retry uploading a failed attachment
  const retryUpload = (attachment: Attachment) => {
    if (attachment.file) {
      // Reset the attachment's status and progress
      setAttachments((prev) =>
        prev.map((att) =>
          att.uid === attachment.uid
            ? { ...att, status: "uploading", progress: 0 }
            : att
        )
      );

      // Start the upload again
      const filename =
        attachment.type === "image"
          ? `image_${attachment.uid}.png`
          : `${attachment.uid}_${attachment.filename}`;
      uploadFileToServer(
        attachment.file,
        filename,
        attachment?.uid || "",
        attachment.type || ""
      );
    }
  };

  // Function to send the message
  const sendMessage = () => {
    // Check if any files are still uploading
    const uploading = attachments.some((att) => att.status === "uploading");

    if (uploading) {
      alert("Please wait for all files to finish uploading before sending.");
      return;
    }

    if (html.length > 0 || attachments.length > 0) {
      // Remove attachments that failed to upload
      const successfulAttachments = attachments.filter(
        (att) => att.status === "uploaded"
      );

      onSend({
        content: html,
        sender_type: "user",
        sender: null,
        createdAt: new Date(),
        attachments: successfulAttachments.map((att:any) => ({
          url: att.url,
          type: att.type,
          filename: att.filename,
          thumbnail: att.thumbnail,
        })),
      });
      setHtml("");
      setAttachments([]);
    }
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((att) => att.uid !== id));
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    };

    const currentRef = contentEditableRef.current;
    if (currentRef) {
      // @ts-ignore
      currentRef.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      if (currentRef) {
        // @ts-ignore
        currentRef.removeEventListener("keydown", handleKeyDown);
      }
    };
  }, [html, attachments]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFilesAdded(droppedFiles);
  };

  const renderAttachmentPreview = (attachment: Attachment, index: number) => {
    const fileExtension = attachment?.filename?.split(".").pop()?.toLowerCase();

    let preview;
    if (attachment.type === "image") {
      preview = (
        <img
          src={attachment.localUrl || attachment.url}
          alt={attachment.filename}
          className="w-8 h-8 object-cover rounded"
        />
      );
    } else if (attachment.type === "video") {
      preview = (
        <video
          src={attachment.localUrl || attachment.url}
          controls
          className="w-8 h-8 object-cover rounded"
        />
      );
    } else if (fileExtension === "pdf") {
      preview = <File className="w-8 h-8 text-red-500" />;
    } else if (fileExtension === "txt") {
      preview = <FileText className="w-8 h-8 text-gray-500" />;
    } else if (
      fileExtension === "xls" ||
      fileExtension === "xlsx" ||
      fileExtension === "csv"
    ) {
      preview = <FileSpreadsheet className="w-8 h-8 text-green-500" />;
    } else {
      preview = (
        <div className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded">
          {attachment?.filename?.slice(0, 5)}
        </div>
      );
    }

    const displayName =
      attachment?.filename?.length || 0 > 10
        ? `${attachment?.filename?.slice(0, 10)}...`
        : attachment.filename;

    return (
      <div
        key={index}
        className="relative flex items-center bg-gray-100 rounded-lg gap-1 p-1.5 overflow-hidden"
      >
        <div className="w-8 h-8">{preview}</div>
        <div
          className={`text-xs truncate ${
            attachment.status === "failed" && "text-danger-400"
          }`}
        >
          {attachment.status === "failed" ? "Upload failed" : displayName}
        </div>
        <button onClick={() => removeAttachment(attachment?.uid || "")}>
          <XCircleIcon className="w-4 h-4 text-gray-300 hover:text-gray-400" />
        </button>
        {attachment.status === "failed" && (
          <button
            onClick={() => retryUpload(attachment)}
            className="ml-1 w-4 h-4 text-gray-300 hover:text-gray-400"
          >
            <ArrowPathIcon className="w-4 h-4" />
          </button>
        )}
        {attachment.status === "uploading" && (
          <div className="absolute bottom-0 left-0 w-full bg-gray-200 h-1">
            <div
              className="bg-secondary-500 h-1"
              style={{ width: `${attachment.progress}%` }}
            ></div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-2">
      <div
        className={`bg-white rounded-xl border border-gray-100 shadow overflow-hidden ${
          dragOver ? "border-dotted border-secondary-500" : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div>
          <ContentEditable
            innerRef={contentEditableRef}
            html={html}
            contentEditable={true}
            onChange={handleChange}
            tagName="div"
            className="w-full p-3 focus:outline-none content-editable cursor-text text-sm"
            // @ts-ignore
            placeholder={
              disable
                ? "Choose one of the options from above or Type your message"
                : "Type your message or drop files here..."
            }
            style={{ minHeight: "50px", maxHeight: "200px", overflowY: "auto" }}
          />
        </div>
        {attachments.length > 0 && (
          <div className="flex gap-2 p-2 overflow-x-scroll">
            {attachments.map((attachment, index) =>
              renderAttachmentPreview(attachment, index)
            )}
          </div>
        )}
        <div className="flex justify-between items-center px-2 pb-2 text-gray-500">
          <div className="flex gap-2 items-center">
            <label className="cursor-pointer">
              <LucidePaperclip className="size-6 p-1 hover:bg-gray-100 rounded-full cursor-pointer" />
              <input
                ref={fileInputRef} // Added ref to the input
                type="file"
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
            <SmileIcon className="size-6 p-1 hover:bg-gray-100 rounded-full cursor-pointer" />
          </div>
          <button
            onClick={sendMessage}
            className={`${
              html.length > 0 || attachments.length > 0
                ? "bg-secondary-500"
                : "bg-gray-400"
            } rounded-full h-8 w-8`}
          >
            <div className="flex justify-center items-center p-1.5">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-white mt-[1px]"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  fillRule="evenodd"
                  d="M1.846 7.151a.75.75 0 0 0-.228 1.376l6.517 3.915l6.22-4.355a.75.75 0 0 1 .86 1.229l-6.22 4.355l1.45 7.463a.75.75 0 0 0 1.372.256L22.792 3.94a.75.75 0 0 0-.793-1.133z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default InputBox;